import { type DateRange, DayPicker, useDayPicker } from 'react-day-picker';

import { ArrowLeftShortIcon, ArrowRightShortIcon } from '../icons';
import { cn } from '../utils';

import { Button, buttonVariants } from './Button';
import { type CalendarProps } from './Calendar';

function MonthYearCaption({ ...props }) {
  const { goToMonth, nextMonth, previousMonth } = useDayPicker();

  return (
    <>
      <Button
        id="next-month"
        aria-label="Next month"
        onClick={() => previousMonth && goToMonth(previousMonth)}
        variant="ghost"
        size="icon"
      >
        <ArrowLeftShortIcon />
      </Button>

      <div className="text-base">
        <span className="first-letter:uppercase">{props.children}</span>
      </div>
      <Button
        id="previous-month"
        aria-label="Previous month"
        onClick={() => nextMonth && goToMonth(nextMonth)}
        variant="ghost"
        size="icon"
      >
        <ArrowRightShortIcon />
      </Button>
    </>
  );
}

function DatePickerCalendar({
  className,
  defaultMonth,
  ...props
}: CalendarProps) {
  return (
    <DayPicker
      hideNavigation
      className={cn('p-2', className)}
      classNames={{
        months:
          'flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0 space-x-4',
        month: 'space-y-4',
        month_caption: 'flex justify-between pb-1 relative items-center',
        caption_label: 'text-sm font-medium',
        nav: 'space-x-1 flex items-center',
        month_grid: 'w-full h-[260px] border-collapse space-y-1',
        weekdays: 'flex',
        weekday: 'rounded-md w-8 font-normal text-[0.8rem]',
        week: 'flex w-full mt-2',
        day: cn(
          buttonVariants({
            variant: 'ghost',
            size: 'icon',
          }),
          'h-8 w-8 font-normal aria-selected:opacity-100',
        ),
        day_button: 'h-[100%] w-[100%] font-normal',
        selected:
          '!bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary',
        today: 'bg-secondary',
        outside:
          'aria-selected:bg-secondary-accent aria-selected:text-secondary-foreground',
        disabled: 'opacity-50',
        range_middle:
          'aria-selected:bg-primary aria-selected:text-primary-foreground !rounded-none',
        range_end: '!rounded-none !rounded-r',
        range_start: '!rounded-none !rounded-l',
        hidden: 'invisible',
      }}
      components={{
        CaptionLabel: (props) => (
          <MonthYearCaption {...props}>{props.children}</MonthYearCaption>
        ),
      }}
      hidden={props.hidden}
      defaultMonth={defaultMonth}
      {...props}
    />
  );
}
DatePickerCalendar.displayName = 'Calendar';

export { DatePickerCalendar, type DateRange };
